import React, { ComponentProps, ComponentType, ReactNode } from 'react';

import PropTypes from 'prop-types';

import { LinkButton } from '@adsk/alloy-react-button';
import { useLocale } from '@adsk/alloy-react-helpers';
import { AlertWarningIcon } from '@adsk/alloy-react-icon';
import theme from '@adsk/alloy-react-theme';

import ActivityCard from '../ActivityCard';

type ActivityLoadMoreErrorProps = ComponentProps<typeof ActivityCard> & {
  children?: ReactNode;
  onTryAgain?: () => void;
};

const DefaultErrorContent: ComponentType<{
  onTryAgain?: () => void;
}> = ({ onTryAgain }) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  return (
    <div
      css={[
        {
          display: 'flex',
          alignItems: 'center',
        },
      ]}
    >
      <AlertWarningIcon />
      <span
        css={[
          theme.typography.bodySmall,
          {
            color: theme.colors.charcoal700,
            padding: '0 8px',
          },
        ]}
      >
        {T.translate('ERROR_LOADING_MORE')}
      </span>
      <LinkButton onClick={onTryAgain}>
        <span css={[theme.typography.bodySmall]}>
          {T.translate('TRY_AGAIN')}
        </span>
      </LinkButton>
    </div>
  );
};

DefaultErrorContent.propTypes = {
  /** Callback to fetch data again */
  onTryAgain: PropTypes.func,
};

/**
 * ActivityLoadMoreError component. Describes an error fetching more activities.
 */
const ActivityLoadMoreError = React.forwardRef<
  HTMLDivElement,
  ActivityLoadMoreErrorProps
>(({ onTryAgain, children, ...props }, ref) => {
  return (
    <ActivityCard ref={ref} {...props}>
      {children || <DefaultErrorContent onTryAgain={onTryAgain} />}
    </ActivityCard>
  );
});

ActivityLoadMoreError.displayName = 'ActivityLoadMoreError';

ActivityLoadMoreError.defaultProps = {
  isFirst: false,
  renderHeader: () => null,
  renderFooter: () => null,
};

ActivityLoadMoreError.propTypes = {
  ...ActivityCard.propTypes,
  /** The error content */
  children: PropTypes.node,
  /** Callback to fetch data again */
  onTryAgain: PropTypes.func,
};

export default ActivityLoadMoreError;
